<template>
  <q-btn
    no-caps
    color="blue-grey-10"
    size="12px"
    padding="sm"
    label="Take payment via Square POS"
    icon="img:providers/square-white.png"
    @click="makeSquarePayment"
  />
</template>

<script>
import { CapacitorSquare } from '@zuc0001/capacitor-square'
import { dineroFromFloat } from 'components/app/accounting/documents/calculationMixin'
import { toSnapshot } from 'dinero.js'
import PaymentMixin from 'components/app/payments/partials/mixins/paymentMixin'

export default {
  mixins: [PaymentMixin],
  props: {
    invoice: {
      required: true,
      type: Object
    },
    locationId: {
      type: String,
      default: null
    }
  },
  emits: ['success'],
  methods: {
    getIOSErrorCodeMessage (code) {
      const messages = {
        amount_invalid_format: 'The request has a missing or invalid amount to charge.',
        amount_too_large: 'The request amount to charge is too large.',
        amount_too_small: 'The request amount to charge is too small.',
        client_not_authorized_for_user: 'Point of Sale versions prior to 4.53 require the developer to guide sellers through OAuth before allowing them to take payments with the Point of Sale API. As of Square Point of Sale 4.53, this error type is deprecated. For more information, see OAuth API Overview.',
        could_not_perform: 'The request couldn\'t be performed. This is usually because there is an unfinished transaction pending in Square Point of Sale. The seller must open Square Point of Sale and complete the transaction before initiating a new request.',
        currency_code_mismatch: 'The currency code provided in the request doesn\'t match the currency associated with the current business.',
        currency_code_missing: 'The currency code provided in the request is missing or invalid.',
        customer_management_not_supported: 'This seller account doesn\'t support customer management and therefore cannot associate transactions with customers.',
        data_invalid: 'The URL sent to Square Point of Sale has missing or invalid information.',
        invalid_customer_id: 'The customer ID provided in the request doesn\'t correspond to a customer signed in to the seller\'s Customer Directory.',
        invalid_tender_type: 'The request included an invalid tender type.',
        no_network_connection: 'The transaction failed because the device has no network connection.',
        not_logged_in: 'A seller isn\'t currently logged in to Square Point of Sale.',
        payment_canceled: 'The seller canceled the payment in Square Point of Sale.',
        unsupported_api_version: 'The installed version of Square Point of Sale doesn\'t support the specified version of the Point of Sale API.',
        unsupported_currency_code: 'The currency code provided in the request isn\'t currently supported by the Point of Sale API.',
        unsupported_tender_type: 'The request included a tender type that isn\'t currently supported by the Point of Sale API.',
        user_id_mismatch: 'The business location currently signed in to Square Point of Sale doesn\'t match the location represented by the location_id you provided in your request.',
        user_not_active: 'The currently signed-in location hasn\'t activated card processing.'
      }
      return messages[code] || null
    },
    getAndroidErrorCodeMessage (code) {
      const messages = {
        CUSTOMER_MANAGEMENT_NOT_SUPPORTED: 'The Square account used doesn\'t support customer management.',
        DISABLED: 'The Point of Sale API isn\'t currently available.',
        ILLEGAL_LOCATION_ID: 'The provided location ID doesn\'t correspond to the location currently logged in to Square Point of Sale.',
        INVALID_CUSTOMER_ID: 'The provided customer ID is invalid.',
        INVALID_REQUEST: 'The information provided in this transaction request is invalid (such as, a required field is missing or malformed).',
        NO_EMPLOYEE_LOGGED_IN: 'Employee management is enabled but no employee is logged in to Square Point of Sale.',
        NO_NETWORK: 'Square Point of Sale was unable to validate the Point of Sale API request because the Android device didn\'t have an active network connection.',
        NO_RESULT: 'Square Point of Sale didn\'t return a transaction result.',
        TRANSACTION_ALREADY_IN_PROGRESS: 'Another Square Point of Sale transaction is already in progress.',
        TRANSACTION_CANCELED: 'The transaction was canceled in Square Point of Sale.',
        UNAUTHORIZED_CLIENT_ID: 'The application with the provided client ID isn\'t authorized to use the Point of Sale API.',
        UNEXPECTED: 'An unexpected error occurs.',
        UNSUPPORTED_API_VERSION: 'The installed version of Square Point of Sale doesn\'t support this version of the Point of Sale SDK.',
        UNSUPPORTED_WEB_API_VERSION: 'The Web API used isn\'t supported in the supplied API version, which must be version 1.3 or later.',
        USER_NOT_ACTIVATED: 'Square Point of Sale tried to process a credit card transaction, but the associated Square account isn\'t activated for card processing.',
        USER_NOT_LOGGED_IN: 'No user is currently logged in to Square Point of Sale.'
      }
      return messages[code] || null
    },
    makeSquarePayment () {
      this.$loading.show()

      // Initiate a transaction
      CapacitorSquare.startTransaction({
        totalAmount: toSnapshot(dineroFromFloat(this.invoice.amount_due)).amount, // amount in pennies/cents
        currencyCode: 'AUD', // ISO currency code, must be support by square
        allowedPaymentMethods: ['CARD'], // Sqaure TendType: https://developer.squareup.com/docs/api/point-of-sale/android/com/squareup/sdk/pos/ChargeRequest.TenderType.html
        autoReturnTimeout: 4000, // The timeout to set in milliseconds, or AutoReturn.NoTimeout. If you specify a timeout, it must be between 3200 milliseconds and 10000 milliseconds.
        callbackUrl: 'miraapp://square-callback', // see iOS setup
        locationId: this.locationId,
        note: `${this.siteName} Invoice - ${this.invoice.invoice_number}`
      }).catch(error => {
        if (String(error).includes('Square Point of Sale is installed on your device')) {
          this.$q.dialog({
            title: 'Not Installed',
            message: 'Please ensure "Square Point of Sale (POS)" is installed on your device'
          })
        }
        else {
          this.$q.dialog({
            title: 'Error',
            message: error
          })
        }
      }).finally(() => {
        this.$loading.hide()
      })
    },
    getIOSTransactionInfo (url) {
      try {
        const data = decodeURI(new URL(url).searchParams.get('data'))
        return JSON.parse(data)
      }
      catch (e) {
        console.log(e)
      }
    },
    handleSquareCallback (data) {
      let dataParsed = data
      if (this.$q.platform.is.ios) {
        dataParsed = this.getIOSTransactionInfo(data.url)
      }

      if (dataParsed['status'] && dataParsed['status'] === 'error') {
        let messageString = null
        if (this.$q.platform.is.ios) {
          messageString = this.getIOSErrorCodeMessage(dataParsed['error_code'])
        }
        if (this.$q.platform.is.android) {
          messageString = this.getAndroidErrorCodeMessage(dataParsed['error_code'])
        }

        this.$q.dialog({
          title: 'Error',
          message: messageString || 'An unknown error occurred'
        })
      }

      if (dataParsed['status'] && dataParsed['status'] === 'ok') {
        this.$emit('success', {
          description: 'Paid via Square App',
          paid_via: this.paymentViaOptions[3],
          transaction_id: this.$q.platform.is.ios ? dataParsed['transaction_id'] : dataParsed['serverTransactionId']
        })
      }
    }
  },
  mounted () {
    this.$bus.on('square:callback', this.handleSquareCallback)
  },
  beforeUnmount () {
    this.$bus.off('square:callback', this.handleSquareCallback)
  }
}

</script>
